import { createLogger } from '../ns-services/logger.js';
import { targetOutcome } from './tour-state.js';

const logger = createLogger('ns-tutorial');

// Finds a [data-tour-id] anywhere under `root`, walking down through every open shadow root, since
// tour targets live a few components deep (ns-app → view-feed → ns-feed). Hidden elements (no box)
// don't count.
export function findTarget(root: ParentNode, tourId: string): HTMLElement | null {
  const found = root.querySelector<HTMLElement>(`[data-tour-id="${tourId}"]`);
  if (found && found.getClientRects().length > 0) return found;

  for (const element of root.querySelectorAll('*')) {
    if (!element.shadowRoot) continue;
    const inner = findTarget(element.shadowRoot, tourId);
    if (inner) return inner;
  }
  return null;
}

const POLL_MS = 150;

// Where the targets are, to tell when they've stopped moving
const placeOf = (elements: HTMLElement[]) =>
  elements
    .map((element) => {
      const { x, y, width, height } = element.getBoundingClientRect();
      return `${x},${y},${width},${height}`;
    })
    .join(';');

// Whether a finite animation (a card's slide, say) is still running on the element or anything it
// sits in, across shadow boundaries. Endless ones (a pulsing badge) never finish, so they don't count.
function stillAnimating(element: Element): boolean {
  for (let node: Node | null = element; node; ) {
    if (node instanceof Element) {
      const running = node
        .getAnimations()
        .some(
          (animation) => animation.playState === 'running' && animation.effect?.getTiming().iterations !== Infinity,
        );
      if (running) return true;
    }
    node = node.parentNode ?? (node instanceof ShadowRoot ? node.host : null);
  }
  return false;
}

// Resolves every target of a step, retrying while the page renders them, and once they're found,
// until no animation is moving them and they're in the same place on two polls running: a card
// sliding in would otherwise get its hole and bubble mid-slide (onboarding's slides pause halfway,
// so the place alone isn't enough). Shadow roots don't report to a MutationObserver on the document, so
// this polls. Resolves to null once the timeout passes with a target still missing (the step is
// then skipped), or when `signal` aborts.
export function waitForTargets(
  root: ParentNode,
  tourIds: string[],
  signal: AbortSignal,
  timeoutMs?: number,
): Promise<HTMLElement[] | null> {
  const started = performance.now();
  let lastPlace: string | undefined;
  return new Promise((resolve) => {
    const attempt = () => {
      if (signal.aborted) return resolve(null);
      const found = tourIds.map((id) => findTarget(root, id)).filter((el): el is HTMLElement => !!el);
      const place = found.length === tourIds.length ? placeOf(found) : undefined;
      const settled = place !== undefined && place === lastPlace && !found.some(stillAnimating);
      lastPlace = place;
      const outcome = targetOutcome(found.length, tourIds.length, performance.now() - started, settled, timeoutMs);
      if (outcome === 'show') return resolve(found);
      if (outcome === 'skip') {
        const missing = tourIds.filter((id) => !findTarget(root, id));
        logger.warn(`Skipped a step, no target for ${missing.join(', ')}`);
        return resolve(null);
      }
      setTimeout(attempt, POLL_MS);
    };
    attempt();
  });
}

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// The elements Tab can reach inside `element`, in document order, through open shadow roots. Web
// Awesome controls keep their native control in their shadow root, so that is what is collected.
export function focusablesIn(element: Element): HTMLElement[] {
  const result: HTMLElement[] = [];
  const visit = (node: Element) => {
    if (node.matches(FOCUSABLE) && (node as HTMLElement).getClientRects().length > 0) result.push(node as HTMLElement);
    if (node.shadowRoot) for (const child of node.shadowRoot.children) visit(child);
    for (const child of node.children) visit(child);
  };
  visit(element);
  return result;
}

// Whether an event's path passes through any of `elements`, across shadow boundaries
export function eventWithin(event: Event, elements: Element[]): boolean {
  const path = event.composedPath();
  return elements.some((element) => path.includes(element));
}
